import { addDays, format, parseISO, startOfWeek } from "date-fns";

import type { SprintRow } from "@/infrastructure/supabase/database.types";

/** A sprint is a week long by default: start + 6 days is inclusive of both ends. */
export const SPRINT_LENGTH_DAYS = 7;

/**
 * Sunday, matching the Israeli work week (Sun–Thu) the delivery team runs on.
 * date-fns already defaults to 0/Sunday; naming it makes the choice explicit.
 */
const WEEK_STARTS_ON = 0;

/** `date` columns are plain yyyy-MM-dd — parse without a timezone shift. */
export function toDateOnly(iso: string): Date {
  return parseISO(iso);
}

export function toIsoDate(date: Date): string {
  return format(date, "yyyy-MM-dd");
}

/** The inclusive end date of a 1-week sprint starting on `startIso`. */
export function weekEndFor(startIso: string): string {
  return toIsoDate(addDays(toDateOnly(startIso), SPRINT_LENGTH_DAYS - 1));
}

/**
 * Where the next sprint should start: the day after the latest sprint ends, so
 * consecutive sprints tile without gaps. With no sprints yet, this week's start.
 */
export function nextSprintStart(sprints: SprintRow[], today: Date): string {
  const latestEnd = sprints.reduce<string | null>(
    (max, s) => (max === null || s.end_date > max ? s.end_date : max),
    null
  );
  if (latestEnd === null) return toIsoDate(startOfWeek(today, { weekStartsOn: WEEK_STARTS_ON }));
  return toIsoDate(addDays(toDateOnly(latestEnd), 1));
}

/**
 * "Sprint 4" — the next ordinal, so creating a sprint needs no typing.
 *
 * Derived from the highest ordinal among the names this app itself generates,
 * not from the count: after deleting Sprint 2 of 1–3, a count-based ordinal
 * would suggest "Sprint 3", which already exists. The pattern is anchored to
 * the exact "Sprint N" format so custom names don't participate — an
 * unanchored trailing-digits match would read "Q3 2026" as ordinal 2026 — so
 * an all-custom-named set falls back to the count.
 */
export function nextSprintName(sprints: SprintRow[]): string {
  let highest = 0;
  for (const s of sprints) {
    const match = /^Sprint\s+(\d+)$/.exec(s.name);
    if (!match?.[1]) continue;
    const n = Number.parseInt(match[1], 10);
    if (n > highest) highest = n;
  }
  return `Sprint ${(highest > 0 ? highest : sprints.length) + 1}`;
}

/** "3–9 Aug" within a month, "28 Jul – 3 Aug" across one. */
export function formatSprintRange(startIso: string, endIso: string): string {
  const start = toDateOnly(startIso);
  const end = toDateOnly(endIso);
  const sameMonth = format(start, "yyyy-MM") === format(end, "yyyy-MM");
  return sameMonth
    ? `${format(start, "d")}–${format(end, "d MMM")}`
    : `${format(start, "d MMM")} – ${format(end, "d MMM")}`;
}

export type SprintTiming = "past" | "current" | "future";

/** Which side of today a sprint falls on — drives the default collapsed state. */
export function sprintTiming(sprint: SprintRow, today: Date): SprintTiming {
  const todayIso = toIsoDate(today);
  if (sprint.end_date < todayIso) return "past";
  if (sprint.start_date > todayIso) return "future";
  return "current";
}
